/**
 * Portal order creation (Story 7-4) and order history (Story 7-5).
 * POST /api/portal/orders, GET /api/portal/me, GET /api/portal/orders, GET /api/portal/orders/{id}.
 */
import api from "./api";

export interface PortalOrderItemRequest {
  product_id: string;
  quantity: number;
}

export interface CreatePortalOrderRequest {
  items: PortalOrderItemRequest[];
  delivery_address?: string | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  notes?: string | null;
}

export interface StockWarningItem {
  product_id: string;
  product_name: string;
  requested: number;
  available: number;
}

export interface PortalOrderItemResponse {
  id: string;
  order_id: string;
  inventory_item_id: string;
  quantity: number;
  unit_price: string;
  line_total: string;
  product_name: string;
}

export interface PortalOrderResponse {
  id: string;
  order_number: string;
  customer_id: string;
  status: string;
  total_amount: string;
  delivery_address: string | null;
  delivery_latitude: number | null;
  delivery_longitude: number | null;
  notes: string | null;
  items: PortalOrderItemResponse[];
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  stock_warnings?: StockWarningItem[] | null;
}

export interface PortalMeResponse {
  delivery_address: string | null;
  delivery_latitude: number | null;
  delivery_longitude: number | null;
  csrf_token: string | null;
}

export async function getPortalMe(): Promise<PortalMeResponse> {
  const response = await api.get<PortalMeResponse>("/portal/me");
  return response.data;
}

export async function createPortalOrder(
  data: CreatePortalOrderRequest,
  csrfToken?: string | null
): Promise<PortalOrderResponse> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const response = await api.post<PortalOrderResponse>("/portal/orders", data, { headers });
  return response.data;
}

// --- Story 7-5: My Orders list and detail ---

export interface PortalOrderListItem {
  id: string;
  order_number: string;
  status: string;
  total_amount: string;
  created_at: string | null;
  item_count: number;
}

export interface PortalOrderListResponse {
  items: PortalOrderListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ListPortalOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
}

export async function listPortalOrders(
  params: ListPortalOrdersParams = {}
): Promise<PortalOrderListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.status) searchParams.set("status", params.status);
  if (params.from_date) searchParams.set("from_date", params.from_date);
  if (params.to_date) searchParams.set("to_date", params.to_date);
  if (params.search) searchParams.set("search", params.search);
  const qs = searchParams.toString();
  const url = qs ? `/portal/orders?${qs}` : "/portal/orders";
  const response = await api.get<PortalOrderListResponse>(url);
  return response.data;
}

export interface OrderStatusHistoryItem {
  status: string;
  previous_status: string | null;
  changed_at: string;
  changed_by: string | null;
  changed_by_name: string | null;
  notes: string | null;
}

export interface PortalOrderDetailResponse {
  id: string;
  order_number: string;
  customer_id: string;
  status: string;
  total_amount: string;
  delivery_address: string | null;
  delivery_latitude: number | null;
  delivery_longitude: number | null;
  notes: string | null;
  items: PortalOrderItemResponse[];
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  created_by_name: string | null;
  customer: { name: string; phone: string; address: string | null };
  status_history: OrderStatusHistoryItem[];
}

export async function getPortalOrder(orderId: string): Promise<PortalOrderDetailResponse> {
  const response = await api.get<PortalOrderDetailResponse>(`/portal/orders/${orderId}`);
  return response.data;
}

// --- Story 7-8: Portal order cancel and update ---

export interface PortalOrderCancelRequest {
  notes?: string | null;
}

export interface PortalOrderUpdateRequest {
  items: PortalOrderItemRequest[];
  delivery_address?: string | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  notes?: string | null;
}

export async function cancelPortalOrder(
  orderId: string,
  body: PortalOrderCancelRequest = {},
  csrfToken?: string | null
): Promise<PortalOrderDetailResponse> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const response = await api.patch<PortalOrderDetailResponse>(
    `/portal/orders/${orderId}/cancel`,
    body,
    { headers }
  );
  return response.data;
}

export async function updatePortalOrder(
  orderId: string,
  data: PortalOrderUpdateRequest,
  csrfToken?: string | null
): Promise<PortalOrderDetailResponse> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const response = await api.put<PortalOrderDetailResponse>(`/portal/orders/${orderId}`, data, {
    headers,
  });
  return response.data;
}
